"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TranslitMapModal = void 0;
const tslib_1 = require("tslib");
const React = tslib_1.__importStar(require("react"));
const nano_theme_1 = require("nano-theme");
const Key_1 = require("@jsonjoy.com/ui/lib/1-inline/Key");
const Tabs_1 = require("@jsonjoy.com/ui/lib/3-list-item/Tabs");
const MiniTitle_1 = require("@jsonjoy.com/ui/lib/3-list-item/MiniTitle");
const Separator_1 = require("@jsonjoy.com/ui/lib/3-list-item/Separator");
const Space_1 = require("@jsonjoy.com/ui/lib/3-list-item/Space");
const context_1 = require("@jsonjoy.com/ui/lib/styles/context");
const ModalOverlay_1 = require("../chrome/ModalOverlay");
const context_2 = require("../context");
const use_t_1 = require("use-t");
const language_1 = tslib_1.__importDefault(require("iconista/lib/react/tabler/language"));
const EmptyIcon = (props) => (React.createElement(language_1.default, { width: 32, height: 32, ...props }));
const splitName = (name) => {
    const m = /^(.+?)\s*\(([^)]+)\)\s*$/.exec(name);
    if (!m)
        return [name, null];
    return [m[1], m[2]];
};
const wrapperClass = (0, nano_theme_1.rule)({
    bxz: 'border-box',
    w: '100%',
    pd: '40px 48px 56px',
});
const introClass = (0, nano_theme_1.rule)({
    maxW: '720px',
    mar: '0 auto 32px',
    fz: '13px',
    lh: '1.5',
    textAlign: 'center',
});
const emptyClass = (0, nano_theme_1.rule)({
    d: 'flex',
    fld: 'column',
    ai: 'center',
    jc: 'center',
    textAlign: 'center',
    pd: '64px 16px',
    gap: '12px',
});
const emptyHeadingClass = (0, nano_theme_1.rule)({
    mar: 0,
    fz: '15px',
    fw: '600',
});
const emptyBodyClass = (0, nano_theme_1.rule)({
    mar: 0,
    maxW: '420px',
    fz: '13px',
    lh: '1.5',
});
const tabsWrapClass = (0, nano_theme_1.rule)({
    w: '100%',
    mar: '0 0 32px',
});
const contentsClass = (0, nano_theme_1.rule)({
    bxz: 'border-box',
    w: '100%',
    maxW: '1444px',
    mar: '0 auto',
});
const gridClass = (0, nano_theme_1.rule)({
    d: 'flex',
    fw: 'wrap',
    jc: 'center',
    ai: 'flex-start',
    gap: '36px 64px',
});
const groupClass = (0, nano_theme_1.rule)({
    miw: 0,
    bxz: 'border-box',
    flex: '0 1 360px',
    maxW: '360px',
});
const groupTitleClass = (0, nano_theme_1.rule)({
    mar: '0 0 10px',
});
const rowsContainerClass = (0, nano_theme_1.rule)({
    bxz: 'border-box',
    w: '100%',
    columnGap: '56px',
});
const rowClass = (0, nano_theme_1.rule)({
    d: 'flex',
    ai: 'center',
    jc: 'space-between',
    gap: '8px',
    pd: '4px 0',
    fz: '13.5px',
    lh: '1.25',
    breakInside: 'avoid',
    WebkitColumnBreakInside: 'avoid',
    pageBreakInside: 'avoid',
});
const inputCellClass = (0, nano_theme_1.rule)({
    fls: '0 0 auto',
    d: 'inline-flex',
    ai: 'center',
    gap: '4px',
});
const arrowCellClass = (0, nano_theme_1.rule)({
    fls: '0 0 auto',
    fz: '11px',
    pd: '0 6px',
});
const outputCellClass = (0, nano_theme_1.rule)({
    fls: '1 1 auto',
    textAlign: 'end',
    fz: '18px',
    fw: '500',
});
const groupRules = (rules) => {
    const single = [];
    const di = [];
    const tri = [];
    for (const r of rules) {
        if (r.in.length === 1)
            single.push(r);
        else if (r.in.length === 2)
            di.push(r);
        else
            tri.push(r);
    }
    const sortByIn = (a, b) => a.in.localeCompare(b.in);
    single.sort(sortByIn);
    di.sort(sortByIn);
    tri.sort(sortByIn);
    const groups = [];
    if (single.length)
        groups.push({ title: 'Single keys', rules: single });
    if (di.length)
        groups.push({ title: 'Digraphs', rules: di });
    if (tri.length)
        groups.push({ title: 'Trigraphs', rules: tri });
    return groups;
};
/** Pick how many sub-columns to flow rules into based on count. */
const subColumns = (count, charWidth) => {
    if (charWidth >= 3)
        return count > 3 ? 2 : 1;
    if (charWidth >= 2)
        return count > 5 ? 2 : 1;
    if (count > 18)
        return 3;
    if (count > 9)
        return 2;
    return 1;
};
const RuleRow = ({ rule }) => {
    const styles = (0, context_1.useStyles)();
    return (React.createElement("div", { className: rowClass },
        React.createElement("span", { className: inputCellClass }, [...rule.in].map((ch, i) => (React.createElement(Key_1.Key, { key: i }, ch === ' ' ? '␠' : ch)))),
        React.createElement("span", { className: arrowCellClass, style: { color: styles.g(0.5, 0.45) } }, '→'),
        React.createElement("span", { className: outputCellClass, style: { color: styles.g(0.1, 0.95) } }, rule.out || React.createElement("em", { style: { opacity: 0.4, fontWeight: 'normal', fontSize: '12px' } }, "(empty)"))));
};
const RuleGroupSection = ({ group }) => {
    const charWidth = group.rules[0]?.in?.length ?? 1;
    const cols = subColumns(group.rules.length, charWidth);
    return (React.createElement("section", { className: groupClass },
        React.createElement("div", { className: groupTitleClass },
            React.createElement(MiniTitle_1.MiniTitle, { component: "div", contrast: true }, group.title)),
        React.createElement(Separator_1.Separator, null),
        React.createElement(Space_1.Space, null),
        React.createElement("div", { className: rowsContainerClass, style: { columnCount: cols } }, group.rules.map((r) => (React.createElement(RuleRow, { key: r.in, rule: r }))))));
};
const FinalForms = ({ scheme }) => {
    const [t] = (0, use_t_1.useT)();
    const styles = (0, context_1.useStyles)();
    if (!scheme.finalForms || !Object.keys(scheme.finalForms).length)
        return null;
    const entries = Object.entries(scheme.finalForms);
    return (React.createElement("section", { className: groupClass },
        React.createElement("div", { className: groupTitleClass },
            React.createElement(MiniTitle_1.MiniTitle, { component: "div", contrast: true }, t('Final forms'))),
        React.createElement(Separator_1.Separator, null),
        React.createElement(Space_1.Space, null),
        entries.map(([from, to]) => (React.createElement("div", { key: from, className: rowClass },
            React.createElement("span", { className: outputCellClass, style: { color: styles.g(0.4, 0.6), textAlign: 'start' } }, from),
            React.createElement("span", { className: arrowCellClass, style: { color: styles.g(0.5, 0.45) } }, '→'),
            React.createElement("span", { className: outputCellClass, style: { color: styles.g(0.1, 0.95) } }, to)))),
        React.createElement("div", { style: { fontSize: '11px', color: styles.g(0.5, 0.55), margin: '6px 0 0' } }, t('Applied to the previous glyph at word boundaries.'))));
};
const EmptyState = () => {
    const [t] = (0, use_t_1.useT)();
    const styles = (0, context_1.useStyles)();
    return (React.createElement("div", { className: emptyClass, style: { color: styles.g(0.4, 0.6) } },
        React.createElement("span", { style: { color: styles.g(0.5, 0.5) } },
            React.createElement(EmptyIcon, null)),
        React.createElement("h3", { className: emptyHeadingClass, style: { color: styles.g(0.2, 0.85) } }, t('Transliteration is off')),
        React.createElement("p", { className: emptyBodyClass }, t('Pick a language above to view its phonetic map and turn the mode on.'))));
};
const Body = ({ scheme }) => {
    const styles = (0, context_1.useStyles)();
    const groups = React.useMemo(() => groupRules(scheme.rules), [scheme]);
    return (React.createElement(React.Fragment, null,
        React.createElement("p", { className: introClass, style: { color: styles.g(0.3, 0.7) } },
            "Type these ASCII keys to enter ",
            React.createElement("strong", null, scheme.name),
            ". Multi-character sequences are matched longest-first \u2014 e.g. typing ",
            React.createElement("code", null, "s"),
            " then ",
            React.createElement("code", null, "h"),
            " rewrites the previous letter when ",
            React.createElement("code", null, "sh"),
            ' ',
            "matches a digraph rule."),
        React.createElement("div", { className: gridClass },
            groups.map((group) => (React.createElement(RuleGroupSection, { key: group.title, group: group }))),
            React.createElement(FinalForms, { scheme: scheme }))));
};
const OFF_TAB_KEY = 'off';
const TranslitMapModal = () => {
    const mutxt = (0, context_2.useMuTxt)();
    const openId = mutxt.translit.mapOpen.use();
    const activeMode = mutxt.translit.active.use();
    const close = React.useCallback(() => mutxt.translit.closeMap(), [mutxt]);
    const schemes = mutxt.translit.list();
    if (!openId)
        return null;
    const active = mutxt.translit.schemes.get(openId) ?? schemes[0];
    if (!active)
        return null;
    const langCounts = new Map();
    for (const s of schemes)
        langCounts.set(s.language, (langCounts.get(s.language) ?? 0) + 1);
    const tabItems = [
        { key: OFF_TAB_KEY, label: 'Off' },
        ...schemes.map((scheme) => {
            const ambiguous = (langCounts.get(scheme.language) ?? 0) > 1;
            const [label, suffix] = splitName(scheme.name);
            return {
                key: scheme.id,
                label: ambiguous && suffix ? `${label} (${suffix})` : label,
            };
        }),
    ];
    const activeTab = activeMode ?? OFF_TAB_KEY;
    return (React.createElement(ModalOverlay_1.ModalOverlay, { open: true, title: "Transliteration", onClose: close },
        React.createElement("div", { className: wrapperClass },
            React.createElement("div", { className: tabsWrapClass },
                React.createElement(Tabs_1.Tabs, { items: tabItems, active: activeTab, onChange: (id) => {
                        if (id === OFF_TAB_KEY) {
                            mutxt.translit.off();
                            return;
                        }
                        mutxt.translit.mapOpen.set(id);
                        mutxt.translit.on(id);
                    }, spread: true, muted: true })),
            React.createElement("div", { className: contentsClass }, activeTab === OFF_TAB_KEY ? React.createElement(EmptyState, null) : React.createElement(Body, { scheme: active })))));
};
exports.TranslitMapModal = TranslitMapModal;
